// ─── CONFIGURACIÓN API ────────────────────────────────────────────────────────────
// Estos valores se inyectan desde Liquid via window.__STOREFRONT_*
// No los hardcodees aquí — vienen del template search.liquid
const STOREFRONT_TOKEN = window.__STOREFRONT_TOKEN__;
const SHOP_DOMAIN = window.__SHOP_DOMAIN__;
const API_VERSION = window.__API_VERSION__ || "2026-07";
const STOREFRONT_URL = `https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`;

// ─── QUERY PRINCIPAL ──────────────────────────────────────────────────────────
const SEARCH_QUERY = `
  query SearchProducts(
    $query: String!
    $first: Int!
    $after: String
    $filters: [ProductFilter!]
    $sortKey: SearchSortKeys
    $reverse: Boolean
    $country: CountryCode!
    $language: LanguageCode!
  ) @inContext(country: $country, language: $language) {
    search(
      query: $query
      first: $first
      after: $after
      productFilters: $filters
      sortKey: $sortKey
      reverse: $reverse
      types: [PRODUCT]
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ... on Product {
            id
            title
            handle
            productType
            tags
            availableForSale
            vendor
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            featuredImage {
                url
                altText
                width
                height
            }
            metafields(identifiers: [
              { namespace: "custom", key: "related_products" }
              { namespace: "custom", key: "color" }
              { namespace: "custom", key: "barefoot_inescop" }
            ]) {
                namespace
                key
                value
                reference {
                    ... on Metaobject {
                        fields {
                            key
                            value
                            reference {
                                ... on MediaImage {
                                    image {
                                        url(transform: { maxWidth: 12, maxHeight: 12 })
                                    }
                                }
                            }
                        }
                    }
                }
                references(first: 10) {
                    edges {
                        node {
                            ... on Product {
                                id
                                handle
                                title
                                availableForSale
                                tags
                                productType
                                featuredImage {
                                    url
                                    altText
                                    width
                                    height
                                }
                                priceRange {
                                    minVariantPrice { amount currencyCode }
                                }
                                compareAtPriceRange {
                                    minVariantPrice { amount currencyCode }
                                }
                                metafields(identifiers: [
                                    { namespace: "custom", key: "color" }
                                ]) {
                                    namespace
                                    key
                                    value
                                    reference {
                                        ... on Metaobject {
                                            fields {
                                                key
                                                value
                                                reference {
                                                ... on MediaImage {
                                                    image {
                                                        url(transform: { maxWidth: 12, maxHeight: 12 })
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
          }
        }
      }
      productFilters {
        id
        label
        type
        values {
          id
          label
          count
          input
        }
      }
    }
  }
`;

// ─── CLIENTE ──────────────────────────────────────────────────────────────────
async function storefrontFetch({ query, variables }) {
    const res = await fetch(STOREFRONT_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
        },
        body: JSON.stringify({ query, variables }),
    });

    if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);

    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0].message);

    return json.data;
}

// ─── FUNCIÓN PRINCIPAL DE BÚSQUEDA ────────────────────────────────────────────
async function searchProducts({
    query = "*",
    first = 24,
    after = null,
    filters = [],
    sortKey = "RELEVANCE",
    reverse = false,
    country = 'ES',
    language = 'ES',
}) {
    const data = await storefrontFetch({
        query: SEARCH_QUERY,
        variables: { query, first, after, filters, sortKey, reverse, country, language },
    });

    const search = data.search;

    return {
        products: search.edges.map((e) => e.node),
        totalCount: search.totalCount,
        hasNextPage: search.pageInfo.hasNextPage,
        endCursor: search.pageInfo.endCursor,
        productFilters: search.productFilters,
    };
}

// ─── LECTURA DE FILTROS DESDE LA URL ─────────────────────────────────────────
// Convierte los params de la URL de Shopify al formato productFilters de la API
// Ejemplo de URL:
//   ?filter.p.m.custom.color=gid://shopify/FilterSettingGroup/326533507
//   &filter.v.option.size=36
//   &filter.p.m.custom.dto_es_pt=30
//   &filter.p.price.gte=20&filter.p.price.lte=80
//   &filter.p.product_type=Bailarinas
//   &filter.p.m.custom.ficha_asociada=Soft  (TODO: confirmar key exacta)

function readFiltersFromURL(targetMarket) {
    const params = new URLSearchParams(window.location.search);
    const filters = [];
    const state = {
        color: null,
        size: null,
        productType: null,
        discount: null,
        barefoot: null,
        priceMin: null,
        priceMax: null,
    };

    for (const [key, value] of params.entries()) {
        // ── Color → productMetafield custom.color ─────────────────────────────
        if (key === "filter.p.m.custom.color") {
            state.color = value;
            filters.push({
                productMetafield: { namespace: "custom", key: "color", value },
            });
        }

        // ── Talla → variantOption size ────────────────────────────────────────
        else if (key === "filter.v.option.size") {
            state.size = value;
            filters.push({
                variantOption: { name: "size", value },
            });
        }

        // ── Tipo de producto → productType ────────────────────────────────────
        else if (key === "filter.p.product_type") {
            state.productType = value;
            filters.push({ productType: value });
        }

        // ── Descuento → metafield según mercado ───────────────────────────────
        // Detectamos cualquiera de los tres params de descuento
        else if (
            key === "filter.p.m.custom.dto_es_pt" ||
            key === "filter.p.m.custom.dto_it_fr_ea_int" ||
            key === "filter.p.m.custom.dto_en"
        ) {
            // Solo aplicamos el del mercado activo
            const marketKey = `filter.p.m.custom.dto_${targetMarket}`;
            if (key === marketKey) {
                state.discount = value;
                filters.push({
                    productMetafield: {
                        namespace: "custom",
                        key: key.replace("filter.p.m.custom.", ""),
                        value,
                    },
                });
            }
        }

        // ── Barefoot / Inescop ────────────────────────────────────────────────
        // TODO: confirmar el param_name exacto que devuelve Shopify para este filtro
        // Basándonos en 'filter.p.m.custom.ficha_asociada' que aparece en el código
        else if (key === "filter.p.m.custom.ficha_asociada") {
            state.barefoot = value;
            filters.push({
                productMetafield: {
                    namespace: "custom",
                    key: "ficha_asociada",
                    value,
                },
            });
        }

        // ── Precio mínimo ─────────────────────────────────────────────────────
        else if (key === "filter.p.price.gte") {
            state.priceMin = parseFloat(value);
        }

        // ── Precio máximo ─────────────────────────────────────────────────────
        else if (key === "filter.p.price.lte") {
            state.priceMax = parseFloat(value);
        }
    }

    // Precio como un único objeto (la API lo espera así)
    if (state.priceMin !== null || state.priceMax !== null) {
        const price = {};
        if (state.priceMin !== null) price.min = state.priceMin;
        if (state.priceMax !== null) price.max = state.priceMax;
        filters.push({ price });
    }

    return { filters, state };
}

// ─── ORDENACIÓN ───────────────────────────────────────────────────────────────
// Mapeo sort_by de Shopify → parámetros de la Storefront API
const SORT_MAP = {
    relevance: { sortKey: "RELEVANCE", reverse: false },
    "price-asc": { sortKey: "PRICE", reverse: false },
    "price-desc": { sortKey: "PRICE", reverse: true },
    // 'best-sellers' no tiene sortKey nativo en search
    // se puede aproximar con RELEVANCE o gestionar aparte
};

function getSortFromURL() {
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get("sort_by") || "relevance";
    return SORT_MAP[sortBy] || SORT_MAP["relevance"];
}

// ─── AGRUPACIÓN BAREFOOT ──────────────────────────────────────────────────────
// Reordena los productos según typeOrder del metaobject,
// tratando los productos con tag 'Barefoot' como tipo 'BAREFOOT'
function sortProductsByTypeOrder(products, typeOrder) {
    return [...products].sort((a, b) => {
        const aType = a.tags.includes("Barefoot") ? "BAREFOOT" : a.productType;
        const bType = b.tags.includes("Barefoot") ? "BAREFOOT" : b.productType;
        const aOrder = typeOrder.indexOf(aType);
        const bOrder = typeOrder.indexOf(bType);
        // Si no está en el orden configurado, va al final
        return (aOrder === -1 ? 999 : aOrder) - (bOrder === -1 ? 999 : bOrder);
    });
}

// Exportar al scope global para que Alpine pueda usarlas
// (sin bundler, usamos window en vez de ES modules)
window.SearchAPI = {
    searchProducts,
    readFiltersFromURL,
    getSortFromURL,
    sortProductsByTypeOrder,
};

// ─── CARGAR COLOR MAP DESDE STOREFRONT API ───────────────────────────────────
async function loadColorMap() {
    if (window.__COLOR_MAP_LOADED__) return;

    const query = `
        query GetColors($after: String) {
            metaobjects(type: "color", first: 250, after: $after) {
                pageInfo { hasNextPage endCursor }
                edges {
                    node {
                        label: field(key: "label") { value }
                        color: field(key: "color") { value }
                        image: field(key: "image") { 
                            reference {
                                ... on MediaImage {
                                image { url(transform: { maxWidth: 12, maxHeight: 12 }) }
                                }
                            }
                        }
                    }
                }
            }
        }
    `;

    let allColors = {};
    let after = null;
    let hasNextPage = true;

    while (hasNextPage) {
        const data = await storefrontFetch({ query, variables: { after } });
        const { edges, pageInfo } = data.metaobjects;

        edges.forEach(({ node }) => {
            const label = node.label?.value;
            if (!label) return;
            allColors[label] = {
                color:    node.color?.value    || '',
                imageUrl: node.image?.reference?.image?.url || null,
            };
        });

        hasNextPage = pageInfo.hasNextPage;
        after       = pageInfo.endCursor;
    }

    window.__COLOR_MAP__        = { ...allColors, ...(window.__COLOR_MAP__ || {}) };
    window.__COLOR_MAP_LOADED__ = true;
}
